import { Phone } from 'iconoir-react'
import { useMemo } from 'react'
import AddActivityButton from '../shared/styled/AddActivityButton'
import { Tooltip } from '../Tooltip'
import { useOutboundNumbers } from './useOutboundNumbers'
import type { ContactJson } from 'app/types/entities'

type ClickToCallButtonProps = {
  contact: ContactJson
  onClick: () => void
  iconOnly?: boolean
}

export function ClickToCallButton({ contact, onClick, iconOnly = false }: ClickToCallButtonProps) {
  const [location] = useOutboundNumbers(contact.locationId)
  const { isDisabled, tooltipMessage, isHidden } = useMemo(() => getButtonState(contact, location), [contact, location])

  if (isHidden) return null

  const button = (
    <AddActivityButton
      mode="secondary"
      label="Call"
      Icon={Phone}
      isDisabled={isDisabled}
      onClick={onClick}
      iconOnly={iconOnly}
    />
  )

  if (isDisabled && tooltipMessage) {
    return <Tooltip message={tooltipMessage}>{button}</Tooltip>
  }

  return button
}

function getButtonState(
  contact: ContactJson,
  location?: { officeMainNumber: string; primaryTrackingNumber: string; hasCallRail: boolean },
) {
  if (!location?.hasCallRail) {
    return { isDisabled: true, tooltipMessage: undefined, isHidden: true }
  }

  if (!location.primaryTrackingNumber) {
    return {
      isDisabled: true,
      tooltipMessage: `No primary tracking number configured for this location`,
      isHidden: false,
    }
  }

  const phones = [contact.cellPhone, contact.homePhone, contact.workPhone].filter(Boolean) as string[]
  const validPhones = phones.filter((phone) => phone.replace(/\D/g, ``).length === 10)

  if (validPhones.length === 0) {
    const hasAnyPhone = phones.length > 0
    return {
      isDisabled: true,
      tooltipMessage: hasAnyPhone ? `No valid US/Canada phone numbers available` : `No phone numbers available`,
      isHidden: false,
    }
  }

  return { isDisabled: false, tooltipMessage: undefined, isHidden: false }
}
